import { useEffect } from 'react';
import { useAccount, useConfig } from 'wagmi';
import { BUTTON_LABELS } from '../../constants/buttonLabels';
import { useActiveSigner } from '../../hooks/useActiveSigner';
import { useDataForSigning } from '../../hooks/useDataForSigning';
import { useModalState } from '../../hooks/useModalState';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useSubaccountBalance } from '../../hooks/useSubaccountBalance';
import { SubaccountData, useWalletStorage } from '../../hooks/useWalletStorage';
import { showToast } from '../../lib/showToast';
import { enableOneClickTradingSuccessToastMessage } from '../../lib/toasts';
import { assertRequiredData } from '../../lib/typeGuards';
import { Alert } from '../Alert/Alert';
import { Button } from '../catalyst-ui-kit/button';
import { Loader } from '../Loader';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { useNewSigner } from '../SettingsModal/signerHooks';

interface EnableTradingModalUiProps {
  isLoading: boolean;
  hasDeposited: boolean;
  hasSubaccount: boolean;
  hasRequiredDataErrors: boolean | undefined;
  hasActiveSigner: boolean;
  mutationPending: boolean;
  hasEnabledTrading: boolean;
  onConfirm: () => void;
  onStartTrading: () => void;
  onClose: () => void;
}

export const EnableTradingModalUi: React.FC<EnableTradingModalUiProps> = ({
  isLoading,
  hasDeposited,
  hasSubaccount,
  hasRequiredDataErrors,
  mutationPending,
  hasActiveSigner,
  hasEnabledTrading,
  onConfirm,
  onStartTrading,
  onClose,
}) => {
  const isOpen = useModalState((state) => state.signingModal === 'open');
  const waitingForActiveSigner = mutationPending && !hasActiveSigner;

  const renderWarning = () => {
    if (hasRequiredDataErrors) {
      return (
        <Alert variant="error">
          <span className="text-xs text-contentSecondary">
            Error loading required data for signing. Our team has been notified and working on a
            fix.
          </span>
        </Alert>
      );
    }
    if (!hasDeposited) {
      return <Alert variant="warning">You need to make a deposit before enabling trading.</Alert>;
    }

    // User has deposited, we're just waiting for the indexer to pick up the deposit and create the subaccount.
    if (!hasSubaccount) {
      return (
        <Alert variant="info" icon={<Loader className="size-4" />}>
          <span className="text-xs text-contentSecondary">
            Creating subaccount. It should only take a few seconds...
          </span>
        </Alert>
      );
    }

    if (waitingForActiveSigner) {
      return (
        <Alert variant="info">
          <span className="text-xs text-contentSecondary">
            Enabling One-Click trading can take up to 15 seconds...
          </span>
        </Alert>
      );
    }

    if (hasEnabledTrading) {
      return <Alert variant="success">One-Click Trading has been successfully enabled</Alert>;
    }

    return null;
  };

  return (
    <ResponsiveModal onClose={onClose} open={isOpen} title="Enable trading">
      <div className="flex flex-col space-y-4">
        <div className="space-y-2 text-xs font-medium text-contentSecondary">
          <p>
            With One-Click Trading a single approval at the start of your session lets you execute
            trades instantly—no repeated confirmations required.
          </p>
        </div>
        <Alert>You can disable One-Click Trading in settings</Alert>
        {renderWarning()}
        {!isLoading ? (
          <Button
            color="blue"
            className="w-full text-base sm:text-base"
            onClick={hasEnabledTrading ? onStartTrading : onConfirm}
            disabled={
              !hasEnabledTrading &&
              (!hasSubaccount ||
                !hasDeposited ||
                isLoading ||
                hasRequiredDataErrors ||
                waitingForActiveSigner)
            }
          >
            {waitingForActiveSigner ? (
              <div className="flex items-center gap-2">
                <Loader className="inline size-2" />
                Enabling One-Click Trading...
              </div>
            ) : hasEnabledTrading ? (
              BUTTON_LABELS.action.startTrading
            ) : (
              BUTTON_LABELS.action.enableOneClickTrading
            )}
          </Button>
        ) : (
          <div className="h-9 w-full animate-pulse rounded bg-backgroundTertiary" />
        )}
      </div>
    </ResponsiveModal>
  );
};

export const EnableTradingModal: React.FC<{
  onComplete: (subaccountData: SubaccountData) => void;
}> = ({ onComplete }) => {
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: subaccountBalances, isLoading: isDepositBalanceLoading } = useSubaccountBalance(
    selectedSubaccount?.id
  );
  const { address } = useAccount();
  const { getSubaccountData } = useWalletStorage();
  const config = useConfig();
  const isOpen = useModalState((state) => state.signingModal === 'open');
  const setSigningModal = useModalState((state) => state.setSigningModal);

  const { domain, signedTypedDataTypes, error } = useDataForSigning();
  const amountDeposited = subaccountBalances?.reduce((acc, { amount }) => acc + amount, 0n) || 0n;
  const { data: activeSigner } = useActiveSigner();

  const { mutate, reset, isPending, isSuccess } = useNewSigner('enable');
  useEffect(() => {
    if (error && !isOpen) {
      reset();
    }
  }, [error, isOpen, reset]);
  const hasActiveSigner = Boolean(activeSigner);

  const subaccountData = getSubaccountData(address, selectedSubaccount?.id);

  useEffect(() => {
    if (isSuccess) {
      showToast(enableOneClickTradingSuccessToastMessage());
    }
  }, [isSuccess]);

  // If the connected account changes, reset the form
  useEffect(() => {
    reset();
  }, [address, reset]);

  const handleClose = () => {
    setSigningModal('closed');
    // Avoid resetting the form before the modal is hidden
    setTimeout(() => {
      reset();
    }, 100);
  };

  return (
    <EnableTradingModalUi
      isLoading={isDepositBalanceLoading}
      hasDeposited={amountDeposited > 0}
      hasSubaccount={!!selectedSubaccount}
      hasRequiredDataErrors={Boolean(error)}
      mutationPending={isPending}
      hasActiveSigner={hasActiveSigner}
      hasEnabledTrading={isSuccess}
      onStartTrading={() => {
        if (subaccountData) {
          onComplete(subaccountData);
          handleClose();
        }
      }}
      onConfirm={async () => {
        const params = {
          subaccountName: selectedSubaccount?.name,
          subaccountId: selectedSubaccount?.id,
          address,
          domain,
          signedTypedDataTypes,
          config,
        };

        assertRequiredData(params);

        mutate(params);
      }}
      onClose={handleClose}
    />
  );
};
